import { useState } from "react";
import CardColorBlock from "../../../components/cards/CardColorBlock/CardColorBlock";
import HighlightText from "../../../components/texts/HighlightText/HighlightText";
import Title from "../../../components/common/Title/Title";
import SEO from "../../../components/common/SEO/SEO";
import Lightbox from "../../../components/articles/LightBox/LightBox";
import OptimizedImage from "../../../components/common/OptimizedImage/OptimizedImage";
import { useTranslation, Trans } from "react-i18next";

export default function IIoT() {
    const [lightboxImage, setLightboxImage] = useState(null);
    const { t } = useTranslation(['iiot']);

    const seo = t('seo', { returnObjects: true }) || {};
    const hero = t('hero', { returnObjects: true }) || {};
    const cards = t('cards', { returnObjects: true }) || {};
    const sections = t('sections', { returnObjects: true }) || {};

    return (
        <>
            <SEO
                title={seo.title}
                description={seo.description}
                url="https://www.aivhy.com/expertise/iiot"
                image="https://www.aivhy.com/images/logos/LOGO_AIVHY.webp"
            />
            <div className="main-container">
                <main className="head-content">
                    <div className="column-content-container height-container">
                        <Title text={hero.title} size={["4rem", "3rem", "2rem"]} level="h1" />
                        <HighlightText
                            text={hero.tagline}
                            size={["1.4rem", "1.2rem", "1rem"]} 
                            padding={["50px", "30px 24px", "20px"]}
                            fontWeight="500"
                            alignItems="flex-start"
                        />
                        <p className="paragraph-content">
                            <Trans
                                i18nKey="hero.intro"
                                ns="iiot"
                                components={[
                                    <a className="text-link" href="/expertise/cyber-security" />,
                                    <strong />,
                                ]}
                            />
                        </p>
                    </div>
                    <div className="cards-container">
                        <CardColorBlock image={"/images/icons/brain_white.webp"} color={"var(--color-orange)"} title={cards?.connect?.title} text={cards?.connect?.text} />
                        <CardColorBlock image={"/images/icons/secure_white.webp"} color={"var(--color-blue)"} title={cards?.secure?.title} text={cards?.secure?.text} />
                        <CardColorBlock iconClassName="fas fa-oil-can" iconStyle={{ fontSize: "70px", color: "white" }} color={"var(--color-blue)"} title={cards?.legacy?.title} text={cards?.legacy?.text} />
                        <CardColorBlock image={"/images/icons/robot_arm_white.webp"} color={"var(--color-blue)"} title={cards?.industry40?.title} text={cards?.industry40?.text} />
                    </div>
                </main>

                <section className="page-content-container">
                    <div className="row-content-container">
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                width: "100%",
                            }}
                        >
                            <Title text={sections?.smartWay?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.smartWay.p1"
                                    ns="iiot"
                                    components={[<strong />]}
                                />
                            </p>
                            <p className="paragraph-content">{sections?.smartWay?.p2}</p>
                        </div>
                        <div style={{ width: "100%", display: "flex", alignSelf: "center" }}>
                            <img
                                className="image-clickable"
                                src={sections?.smartWay?.image}
                                alt={sections?.smartWay?.alt}
                                style={{ maxWidth: '100%', height: 'auto' }}
                                onClick={() => setLightboxImage({ src: sections?.smartWay?.image, alt: sections?.smartWay?.alt })}
                                role="button"
                                tabIndex={0}
                                aria-label={`Open ${sections?.smartWay?.alt}`}
                            />
                        </div>
                    </div>
                    <div className="row-content-container">
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                width: "100%",
                            }}
                        >
                            <Title text={sections?.useCases?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <p className="paragraph-content">{sections?.useCases?.intro}</p>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "5px",
                                    marginLeft: "20px",
                                }}
                            >
                                {Array.isArray(sections?.useCases?.bullets) &&
                                    sections.useCases.bullets.map((b, idx) => (
                                        <HighlightText key={idx} text={b} size={["1rem", "0.8rem"]} />
                                    ))}
                            </div>
                        </div>
                        <div style={{ width: "100%" }}>
                            <Title text={sections?.iiot?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <p className="paragraph-content">{sections?.iiot?.body}</p>
                        </div>
                    </div>
                    <div className="row-content-container" style={{ alignItems: "center" }}>
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                gap: "20px",
                                justifyContent: "center",
                            }}
                        >
                            <OptimizedImage
                                src="/images/industries/boxes.webp"
                                className="boxes-mask"
                                alt="Boxes"
                            />
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "30px",
                                width: "100%",
                            }}
                        >
                            <div>
                                <Title text={sections?.howCost?.how?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                                <p className="paragraph-content">
                                    <Trans
                                        i18nKey="sections.howCost.how.body"
                                        ns="iiot"
                                        components={[
                                            <a className="text-link" href="/solutions/implementation" />,
                                        ]}
                                    />
                                </p>
                            </div>
                            <div>
                                <Title text={sections?.howCost?.cost?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                                <p className="paragraph-content">{sections?.howCost?.cost?.body}</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
        </>
    );
}